import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import { collectionGroup, collection, query, orderBy, limit, getDocs, startAfter, QueryDocumentSnapshot, onSnapshot, Unsubscribe, DocumentData } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { Activity, SystemSettings } from '../../types';

interface FeedContextType {
  // Global feed
  activities: Activity[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  refreshFeed: () => Promise<void>;
  loadMore: () => Promise<void>;
  // User's own activities
  myActivities: Activity[];
  myActivitiesLoading: boolean;
}

const FeedContext = createContext<FeedContextType | undefined>(undefined);

export const useFeed = () => {
  const context = useContext(FeedContext);
  if (!context) {
    throw new Error('useFeed must be used within a FeedProvider');
  }
  return context;
};

interface FeedProviderProps {
  children: ReactNode;
  systemSettings?: SystemSettings;
  currentUserId?: string; // NEW: User ID for myActivities query
  suspendedUserIds?: string[]; // NEW: Filter out suspended users
}

const PAGE_SIZE = 20;
const REALTIME_LIMIT = 30; // Real-time listener covers first 30 activities

export const FeedProvider: React.FC<FeedProviderProps> = ({ children, systemSettings, currentUserId, suspendedUserIds = [] }) => {
  // Global feed state
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const unsubscribeRef = useRef<Unsubscribe | null>(null);
  const isInitialLoad = useRef(true);

  // NEW: User's own activities state
  const [myActivities, setMyActivities] = useState<Activity[]>([]);
  const [myActivitiesLoading, setMyActivitiesLoading] = useState(true);
  const myUnsubscribeRef = useRef<Unsubscribe | null>(null);

  // Helper to transform doc to Activity (for collectionGroup queries)
  const transformDoc = useCallback((d: QueryDocumentSnapshot<DocumentData>): Activity => {
    const userId = d.ref.parent.parent?.id;
    return {
      id: d.id,
      ...d.data(),
      userId
    } as Activity & { userId: string };
  }, []);

  // Helper to transform doc for user's own activities (userId is known)
  const transformMyDoc = useCallback((d: QueryDocumentSnapshot<DocumentData>, userId: string): Activity => {
    return {
      id: d.id,
      ...d.data(),
      userId
    } as Activity & { userId: string };
  }, []);

  // Helper to filter activities by date
  const filterActivities = useCallback((acts: Activity[]) => {
    if (!systemSettings) return acts;
    const { eventStartDate, eventEndDate } = systemSettings;

    return acts.filter(a => {
      // 1. Basic checks
      if (a.isDeleted) return false;

      // 2. Filter suspended users
      // Note: myActivities (from user profile) shouldn't be filtered by this context logic
      // if we are visiting that specific user's profile, but for global feed it should.
      // However, currentUserId is usually the viewer.
      // The context is "Global Feed". Suspended users shouldn't show up here.
      if (typeof a.userId === 'string' && suspendedUserIds.includes(a.userId)) return false;

      // 3. Date checks
      const actDate = new Date(a.date);

      if (eventStartDate) {
        const start = new Date(eventStartDate);
        start.setHours(0, 0, 0, 0);
        if (actDate < start) return false;
      }
      if (eventEndDate) {
        const end = new Date(eventEndDate);
        end.setHours(23, 59, 59, 999);
        if (actDate > end) return false;
      }
      return true;
    });
  }, [systemSettings, suspendedUserIds]);

  // Real-time listener for first REALTIME_LIMIT activities (global feed)
  useEffect(() => {
    const q = query(
      collectionGroup(db, 'activities'),
      orderBy('date', 'desc'),
      limit(REALTIME_LIMIT)
    );

    // Reset state for new subscription (e.g. when systemSettings changes)
    isInitialLoad.current = true;
    setLoading(true);

    unsubscribeRef.current = onSnapshot(q, (snapshot) => {
      if (isInitialLoad.current) {
        // First load: set all activities from snapshot
        const allActivities = snapshot.docs.map(transformDoc);
        const validActivities = filterActivities(allActivities);
        setActivities(validActivities);
        setLoading(false);
        isInitialLoad.current = false;

        // Set cursor for pagination
        const lastVisible = snapshot.docs[snapshot.docs.length - 1];
        if (lastVisible) setLastDoc(lastVisible);
        setHasMore(snapshot.docs.length >= REALTIME_LIMIT);
      } else {
        // Subsequent updates: process changes efficiently
        snapshot.docChanges().forEach((change) => {
          const changedActivity = transformDoc(change.doc);
          const isValid = filterActivities([changedActivity]).length > 0;

          if (change.type === 'modified') {
            // Update existing activity in place
            setActivities(prev =>
              prev.map(a => a.id === changedActivity.id ? changedActivity : a)
            );
          } else if (change.type === 'added' && isValid) {
            // New activity - add to top if not already present
            setActivities(prev => {
              if (prev.some(a => a.id === changedActivity.id)) return prev;
              // Insert at correct position by date
              const newList = [changedActivity, ...prev];
              return newList.sort((a, b) =>
                new Date(b.date).getTime() - new Date(a.date).getTime()
              );
            });
          } else if (change.type === 'removed') {
            setActivities(prev => prev.filter(a => a.id !== changedActivity.id));
          }
        });
      }
    }, (error) => {
      console.error("Feed snapshot error:", error);
      setLoading(false);
    });

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [systemSettings?.eventStartDate, systemSettings?.eventEndDate, transformDoc, filterActivities]);

  // NEW: Real-time listener for user's own activities
  useEffect(() => {
    if (!currentUserId) {
      setMyActivities([]);
      setMyActivitiesLoading(false);
      return;
    }

    const q = query(
      collection(db, 'users', currentUserId, 'activities'),
      orderBy('date', 'desc')
    );

    myUnsubscribeRef.current = onSnapshot(q, (snapshot) => {
      const allActivities = snapshot.docs.map(d => transformMyDoc(d, currentUserId));
      const validActivities = filterActivities(allActivities);
      setMyActivities(validActivities);
      setMyActivitiesLoading(false);
    }, (error) => {
      console.error("My activities snapshot error:", error);
      setMyActivitiesLoading(false);
    });

    return () => {
      if (myUnsubscribeRef.current) {
        myUnsubscribeRef.current();
      }
    };
  }, [currentUserId, systemSettings?.eventStartDate, systemSettings?.eventEndDate, transformMyDoc, filterActivities]);

  // Load more uses getDocs for pagination beyond real-time window
  const loadMore = async () => {
    if (!hasMore || loadingMore || loading || !lastDoc) return;

    try {
      setLoadingMore(true);

      const q = query(
        collectionGroup(db, 'activities'),
        orderBy('date', 'desc'),
        startAfter(lastDoc),
        limit(PAGE_SIZE)
      );

      const snapshot = await getDocs(q);
      const fetchedDocs = snapshot.docs;
      const lastVisible = fetchedDocs[fetchedDocs.length - 1];

      if (fetchedDocs.length < PAGE_SIZE) {
        setHasMore(false);
      }

      if (lastVisible) setLastDoc(lastVisible);

      const rawActivities = fetchedDocs.map(transformDoc);
      const validActivities = filterActivities(rawActivities);

      // Append unique activities only
      setActivities(prev => {
        const existingIds = new Set(prev.map(a => a.id));
        const newActs = validActivities.filter(a => !existingIds.has(a.id));
        return [...prev, ...newActs];
      });
    } catch (error) {
      console.error("Feed Fetch Error:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const refreshFeed = async () => {
    // Reset state and let onSnapshot repopulate
    isInitialLoad.current = true;
    setLastDoc(null);
    setHasMore(true);
    setLoading(true);
    setActivities([]);

    // Unsubscribe and resubscribe will happen via useEffect cleanup/rerun
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
  };

  return (
    <FeedContext.Provider value={{
      activities,
      loading,
      loadingMore,
      hasMore,
      refreshFeed,
      loadMore,
      myActivities,
      myActivitiesLoading
    }}>
      {children}
    </FeedContext.Provider>
  );
};
